import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, getAdminToken, setAdminToken } from "../../lib/adminApi";
import { formatApiErrorDetail } from "../../lib/api";
import HbeLogo from "../../components/HbeLogo";

export default function AdminLogin() {
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const redirectTo = loc.state?.from || "/admin";

  // If already authenticated, jump straight to intended page.
  useEffect(() => {
    if (!getAdminToken()) return;
    adminApi.get("/admin/me").then(() => nav(redirectTo, { replace: true })).catch(() => {});
  }, [nav, redirectTo]);

  async function onSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const r = await adminApi.post("/admin/login", { email, password });
      setAdminToken(r.data.access_token);
      toast.success("Welcome back.");
      nav(redirectTo, { replace: true });
    } catch (err) {
      // Never leak "email not found" vs "wrong password".
      if (err?.response?.status === 401) setError("Invalid credentials");
      else setError(formatApiErrorDetail(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center p-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <HbeLogo variant="full" color="navy" size={96} />
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-hbe-accent">Admin</div>
          <h1 className="mt-6 text-2xl md:text-3xl text-hbe-primary font-semibold">Sign in</h1>
          <p className="mt-2 text-sm text-hbe-ink/65">
            Access the HBE Entertainment control panel.
          </p>
        </div>

        <form onSubmit={onSubmit} className="hbe-card p-6 md:p-8 space-y-4" noValidate>
          <div>
            <label className="hbe-label" htmlFor="al-email">
              Email
            </label>
            <input
              id="al-email"
              type="email"
              autoComplete="email"
              className="hbe-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="hbe-label" htmlFor="al-password">
              Password
            </label>
            <input
              id="al-password"
              type="password"
              autoComplete="current-password"
              className="hbe-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
            />
          </div>
          {error && (
            <div
              className="text-sm text-red-700"
              role="alert"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="hbe-btn-primary w-full"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
